import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/shared/components/ui/button";
import { useFiatDepositAddress } from "../../../services";

interface DepositFundsCreditBankDetailProps {
  onSubmit(): void;
}

type CopyableField = "iban" | "reference" | null;

const normalizeValue = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

export default function DepositFundsBankDetail({
  onSubmit,
}: Readonly<DepositFundsCreditBankDetailProps>) {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch, isFetching } =
    useFiatDepositAddress();

  const [copiedField, setCopiedField] = useState<CopyableField>(null);
  const resetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, []);

  const iban = useMemo(() => normalizeValue(data?.iban), [data?.iban]);
  const reference = useMemo(
    () => normalizeValue(data?.reference),
    [data?.reference]
  );
  const currency = useMemo(
    () => normalizeValue(data?.currency),
    [data?.currency]
  );
  const currencyDisplay = currency ? currency.toUpperCase() : "";

  const handleCopy = async (
    value: string,
    field: Exclude<CopyableField, null>
  ) => {
    if (!value) {
      toast.error("No value available to copy.");
      return;
    }

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }

      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      toast.success("Copied to clipboard.");

      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
      }

      resetTimerRef.current = window.setTimeout(() => {
        setCopiedField(null);
        resetTimerRef.current = null;
      }, 2000);
    } catch {
      toast.error("Unable to copy. Please try again.");
    }
  };

  const isRefetching = isFetching && !isLoading;
  const hasData = !!iban || !!reference || !!currencyDisplay;
  const disableSubmit = isLoading || !!error || !hasData;

  return (
    <div className="w-full space-y-4">
      <header className="space-y-2">
        <p className="text-lg text-white font-medium">
          Transfer Funds to Nommia
        </p>
        <p className="text-sm text-label-dark">
          Use the bank details below to complete your transfer. Make sure to
          include the reference exactly as shown.
        </p>
      </header>

      <section className="space-y-3">
        <p className="text-lg text-white font-medium">Bank Details</p>
        <div className="bg-main-dark text-white p-4 border border-gray-700 rounded-lg w-full">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-label-dark">
              <Loader2 className="size-4 animate-spin" /> Fetching bank details…
            </div>
          ) : error ? (
            <div className="space-y-3 py-2 text-sm">
              <p className="text-red-400">
                Unable to load bank transfer details right now. Please try
                again.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void refetch();
                }}
                disabled={isRefetching}
                className="self-start">
                <RefreshCcw className="size-3" />
                {isRefetching ? "Retrying…" : "Retry"}
              </Button>
            </div>
          ) : hasData ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-700 border-dashed pb-3">
                <div className="flex flex-col">
                  <span className="text-xs uppercase tracking-wide text-label-dark">
                    Account Name
                  </span>
                  <span className="text-base text-white font-medium">
                    Nommia Global Ltd
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-700 border-dashed pb-3">
                <div className="flex flex-col">
                  <span className="text-xs uppercase tracking-wide text-label-dark">
                    Recipient's Bank Country
                  </span>
                  <span className="text-base text-white font-medium">
                    Lithuania
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-700 border-dashed pb-3">
                <div className="flex flex-col">
                  <span className="text-xs uppercase tracking-wide text-label-dark">
                    Currency
                  </span>
                  <span className="text-base text-white font-medium">
                    {currencyDisplay || "Not specified"}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-700 border-dashed pb-3">
                <div className="flex flex-col">
                  <span className="text-xs uppercase tracking-wide text-label-dark">
                    Destination IBAN
                  </span>
                  <span className="text-sm font-medium text-white break-all">
                    {iban || "Not provided"}
                  </span>
                </div>
                {iban ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(iban, "iban")}
                    className="shrink-0">
                    {copiedField === "iban" ? (
                      <Check className="size-3" />
                    ) : (
                      <Copy className="size-3" />
                    )}
                    {copiedField === "iban" ? "Copied" : "Copy"}
                  </Button>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-700 border-dashed pb-3">
                <div className="flex flex-col">
                  <span className="text-xs uppercase tracking-wide text-label-dark">
                    BIC
                  </span>
                  <span className="text-base text-white font-medium">
                    BZENLT22
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-col">
                  <span className="text-xs uppercase tracking-wide text-label-dark">
                    Deposit Reference
                  </span>
                  <span className="text-sm font-medium text-white break-all">
                    {reference || "Not provided"}
                  </span>
                </div>
                {reference ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(reference, "reference")}
                    className="shrink-0">
                    {copiedField === "reference" ? (
                      <Check className="size-3" />
                    ) : (
                      <Copy className="size-3" />
                    )}
                    {copiedField === "reference" ? "Copied" : "Copy"}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="py-2 text-sm text-label-dark">
              Bank account details are not available right now.
            </div>
          )}
        </div>
      </section>

      <p className="text-xs text-label-dark">
        Include the deposit reference when you initiate the transfer so we can
        match your funds quickly.
      </p>

      <Button
        className="w-full"
        onClick={() => {
          onSubmit();
          navigate("/assets/deposit");
        }}
        disabled={disableSubmit}>
        I Have Transferred
      </Button>
    </div>
  );
}
